// @flow
import Button from '@atlaskit/button';
import Flag, { FlagGroup } from '@atlaskit/flag';
import Tick from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Query, WithApolloClient, compose, graphql, withApollo } from 'react-apollo';
import { useSetState } from 'react-use';
import styled from 'styled-components';
import LoadingOverlay from 'react-loading-overlay';
import Spinner from '@atlaskit/spinner';
import { useThemeObserver } from '@atlaskit/tokens';
import DeleteComponentModal from '../components/virtual-components/DeleteComponentModal';
import Loading from '../components/virtual-components/Loading';
import MergeComponentModal from '../components/virtual-components/MergeComponentModal';
import TableFilters from '../components/virtual-components/TableFilters';
import VirtualComponentEdit from '../components/virtual-components/VirtualComponentEdit';
import VirtualComponentsTable from '../components/virtual-components/VirtualComponentsTable';
import { DELETE_VIRTUAL_COMPONENT, MERGE_VIRTUAL_COMPONENT, SET_ACCESS_AUDIT } from '../graphql/mutations';
import { SYNC_DATA, VIRTUAL_COMPONENTS_QUERY_PAGINATED } from '../graphql/queries';
import useCreateVirtualComponentContext from '../hooks/useVirtualComponentCreation';
import { analytics } from '../services/analytics';
import type { VirtualComponent } from '../types';
import { ErrorParser } from '../utils/ErrorParser';
import { GREEN, RED, SYNC_ERRORS_MESSAGES } from '../utils/consts';
import PaginationContainer from '../components/pagination/PaginationContainer';
import { useJiraUsersProvider } from '../contexts/JiraUsersContext';

const SearchRow = styled.div`
    margin: 0 0 16px;
`;

const SyncView = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    > div {
        max-width: 400px;
        text-align: center;

        img {
            width: 160px;
        }

        button {
            margin-top: 14px;
            font-size: 16px;
        }
    }
`;

const initialDataState = {
    filters: {},
    sorting: {
        property: 'name',
        direction: 'asc',
    },
    pagination: { pageSize: 15, page: 0 },
};

type VirtualComponentsProps = {
    projectKey?: string,
    onSearch: (filters: any) => void,
};

function VirtualComponents(props: WithApolloClient<VirtualComponentsProps>) {
    const { colorMode } = useThemeObserver();
    // VIRTUAL COMPONENTS
    const [virtualComponentsPaginated, setVirtualComponentsPaginated] = useState({ rows: [], count: 0 });
    const [virtualComponents, setVirtualComponents] = useState([]);
    // DELETE COMPONENT
    const [virtualComponentBeingDeleted, setVirtualComponentBeingDeleted] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    // DELETE COMPONENT
    const [virtualComponentBeingMerged, setVirtualComponentBeingMerged] = useState(null);
    const [mergeDuplicates, setMergeDuplicates] = useState(null);

    const [isLoading, setIsLoading] = useState(true);

    // STATE
    const [virtualComponentBeingEdited, setVirtualComponentBeingEdited] = useState(null);
    const [flags, setFlags] = useState([]);

    const [isAccessAuditSet, setIsAccessAuditSet] = useState(false);

    const [dataState, setDataState] = useSetState(initialDataState);

    const refetch = useCallback(() => setDataState({ refetchKey: Date.now() }), [setDataState]);

    const { show: displayCreateVirtualComponentModal } = useCreateVirtualComponentContext();

    const openCreateVirtualComponentModal = useCallback(
        () => displayCreateVirtualComponentModal(refetch),
        [displayCreateVirtualComponentModal, refetch],
    );

    useEffect(() => {
        if (!isAccessAuditSet) {
            props.setAccessAudit().catch((err) => console.error('error setting access audit', err));
            setIsAccessAuditSet(true);
        }
    }, [isAccessAuditSet, props.setAccessAudit]);

    useEffect(() => {
        if (flags.length) {
            const filteredFlags = flags.filter((flag) => {
                return flag.autoDismiss !== undefined && flag.autoDismiss === false;
            });
            if (filteredFlags.length !== flags.length) {
                setTimeout(() => setFlags(filteredFlags), 5000);
            }
        }
    }, [flags]);

    const editVirtualComponent = useCallback((virtualComponent: VirtualComponent): void => {
        setVirtualComponentBeingEdited(virtualComponent);
        analytics.track({
            userId: window.license,
            event: 'accessed_component',
            timestamp: new Date(),
            properties: {
                addon_key: process.env.REACT_APP_KEY,
            },
        });
    }, []);

    const handleSetFlag = (flag) => {
        setFlags([flag]);
    };

    const dismissFlag = () => {
        setFlags(flags.slice(1));
    };

    const _handleDeleteComponent = (id) => {
        setDeleteLoading(true);
        props
            .deleteVirtualComponent({
                variables: { id },
            })
            .then(() => {
                setVirtualComponentBeingEdited(null);
                setVirtualComponentBeingDeleted(null);
                refetch();
                setFlags([
                    {
                        icon: <Tick primaryColor={GREEN} />,
                        title: 'Component deleted successfully.',
                        key: 'success',
                    },
                ]);
            })
            .catch((err) => {
                console.log('delete error: ', err);
                if (err && err.message && SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)]) {
                    setFlags([
                        {
                            icon: <WarningIcon primaryColor={RED} />,
                            title: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].title,
                            description: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].message,
                            key: 'Warning',
                            autoDismiss: false,
                            actions: [
                                {
                                    content: 'Dismiss',
                                    onClick: () => {
                                        setFlags([]);
                                    },
                                },
                            ],
                        },
                    ]);
                } else {
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED} />,
                            title: 'An error occurred.',
                            key: 'error',
                        },
                    ]);
                }
            })
            .finally(() => setDeleteLoading(false));
    };

    const handleMergeComponent = useCallback(
        async (parentComponentId, targetComponentId) => {
            setMergeDuplicates(null);

            try {
                const res = await props.mergeVirtualComponent({
                    variables: { parentComponentId, targetComponentId },
                });

                if (res.data.merge.success) {
                    refetch();
                    setVirtualComponentBeingMerged(null);
                    setFlags([
                        {
                            icon: <Tick primaryColor={GREEN} />,
                            title: 'Components merged successfully.',
                            key: 'success',
                        },
                    ]);
                    analytics.track({
                        userId: window.license,
                        event: 'merged_component',
                        timestamp: new Date(),
                        properties: {
                            addon_key: process.env.REACT_APP_KEY,
                        },
                    });
                } else {
                    setMergeDuplicates(res.data.merge.data);
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED} />,
                            title: 'An error occurred.',
                            key: 'error',
                        },
                    ]);
                }
            } catch (err) {
                console.log('delete error: ', err);
                if (err && err.message && SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)]) {
                    setFlags([
                        {
                            icon: <WarningIcon primaryColor={RED} />,
                            title: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].title,
                            description: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].message,
                            key: 'Warning',
                            autoDismiss: false,
                            actions: [
                                {
                                    content: 'Dismiss',
                                    onClick: () => {
                                        setFlags([]);
                                    },
                                },
                            ],
                        },
                    ]);
                } else {
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED} />,
                            title: "We couldn't finish the update",
                            description:
                                "This usually happens when components are updated from Jira and aren't synchronized yet. If this problem persist contact support on https://apps.appf.re/oc/support/p2",
                            key: 'error',
                            actions: [{ content: 'Synchronize components', onClick: props.synchronize }],
                            autoDismiss: false,
                        },
                    ]);
                }
            }
        },
        [props, refetch],
    );

    const handleFilterChange = (value: Object, meta: Object) => {
        const getFirstPageConfig = (prev) => {
            if (prev?.pagination?.page === 0) {
                return prev;
            }
            return { ...prev, pagination: { ...prev.pagination, page: 0 } };
        };
        switch (meta.action) {
            case 'add':
                setDataState((prev) => ({
                    ...getFirstPageConfig(prev),
                    filters: {
                        ...prev.filters,
                        [meta.key]: meta.data,
                    },
                }));
                break;
            case 'remove':
                setDataState((prev) => {
                    const { filters } = prev;
                    delete filters[meta.key];
                    return {
                        ...getFirstPageConfig(prev),
                        filters: { ...filters },
                    };
                });
                break;
            case 'update':
            case 'clear':
                setDataState((prev) => ({ ...getFirstPageConfig(prev), filters: value }));
                break;
            default:
        }
        analytics.track({
            userId: window.license,
            event: 'filtered_components',
            timestamp: new Date(),
            properties: {
                addon_key: process.env.REACT_APP_KEY,
                component_filter_type: meta.key,
            },
        });
    };

    const { usersCache, setUsersCache } = useJiraUsersProvider();

    useEffect(() => {
        setVirtualComponents(virtualComponentsPaginated.rows);
        setUsersCache((prev) => ({
            ...virtualComponentsPaginated.rows.reduce(
                (acc, curr) => {
                    if (curr.virtualComponentLead?.jiraUserAccountId) {
                        return {
                            ...acc,
                            [curr.virtualComponentLead?.jiraUserAccountId]:
                                acc[curr.virtualComponentLead?.jiraUserAccountId] ?? undefined,
                        };
                    }
                    return acc;
                },
                { ...prev },
            ),
        }));
    }, [setUsersCache, virtualComponentsPaginated.rows]);

    const virtualComponentsWithUsers = useMemo(() => {
        return virtualComponents.map((vc) => ({
            ...vc,
            ...(vc.virtualComponentLead?.jiraUserAccountId
                ? {
                      virtualComponentLead:
                          usersCache[vc.virtualComponentLead.jiraUserAccountId] ?? vc.virtualComponentLead,
                  }
                : {}),
        }));
    }, [usersCache, virtualComponents]);

    const { componentLeadDefaultOptions, componentProjectsDefaultOptions } = useMemo(() => {
        return virtualComponentsWithUsers.reduce(
            (acc, curr) => {
                const currAccountId =
                    curr.virtualComponentLead?.jiraUserAccountId || curr.virtualComponentLead?.accountId;
                if (
                    currAccountId &&
                    currAccountId !== 'unknown' &&
                    !acc.componentLeadDefaultOptions.some(
                        (user) => (user.jiraUserAccountId || user.accountId) === currAccountId,
                    )
                ) {
                    acc.componentLeadDefaultOptions.push(curr.virtualComponentLead);
                }

                const projectsToAdd = (curr.projects ?? []).filter(
                    (proj) =>
                        !acc.componentProjectsDefaultOptions.some(
                            ({ jiraProjectId }) => jiraProjectId === proj.jiraProjectId,
                        ),
                );

                if (projectsToAdd?.length) {
                    acc.componentProjectsDefaultOptions.push(...projectsToAdd);
                }
                return acc;
            },
            { componentLeadDefaultOptions: [], componentProjectsDefaultOptions: [] },
        );
    }, [virtualComponentsWithUsers]);

    useEffect(() => {
        if (virtualComponentBeingEdited) {
            props.putContentToRightSidePanel(
                <VirtualComponentEdit
                    virtualComponentBeingEdited={virtualComponentBeingEdited}
                    editVirtualComponent={editVirtualComponent}
                    handleSetEdited={refetch}
                    handleSetFlag={handleSetFlag}
                    setToDelete={setVirtualComponentBeingDeleted}
                    setToMerge={setVirtualComponentBeingMerged}
                    synchronizeComponents={props.synchronize}
                    skipVirtualLabelsQueryRefetch
                    skipVirtualComponentsQueryRefetch
                    skipVirtualComponentQueryRefetch
                    componentParentsDefaultOptions={virtualComponentsPaginated.rows}
                    componentLeadDefaultOptions={componentLeadDefaultOptions}
                    componentProjectsDefaultOptions={componentProjectsDefaultOptions}
                />,
                null,
                true,
            );
        } else {
            props.hideRightSidePanel();
        }
    }, [virtualComponentBeingEdited, componentLeadDefaultOptions, componentProjectsDefaultOptions]);

    const onChangeSorting = useCallback(
        (property, direction) => setDataState({ sorting: property ? { property, direction } : undefined }),
        [setDataState],
    );

    const onPageChange = useCallback(
        (page, pageSize) => {
            setDataState((prev) => {
                if (prev.pagination.page !== page || prev.pagination.pageSize !== pageSize) {
                    return { ...prev, pagination: { page, pageSize } };
                }
                return prev;
            });
        },
        [setDataState],
    );

    useEffect(() => {
        setIsLoading(true);
        try {
            const labels = (dataState.filters.labels || []).map(({ value }) => value);
            const projects = (dataState.filters.projects || []).map(({ value }) => value);
            const variables = {
                filter: {
                    virtualComponentName: dataState.filters.search,
                    labels,
                    projects,
                    jiraProjectKey: props.projectKey,
                },
                sorting: dataState.sorting,
                pagination: dataState.pagination,
            };
            props.onSearch(variables);
            props.client
                .query({
                    query: VIRTUAL_COMPONENTS_QUERY_PAGINATED,
                    variables,
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => {
                    setVirtualComponentsPaginated(data.virtualComponentsPaginated);
                    setIsLoading(false);
                });
        } catch {
            setIsLoading(false);
        }
    }, [
        dataState.filters.labels,
        dataState.filters.projects,
        dataState.filters.search,
        dataState.pagination,
        dataState.refetchKey,
        dataState.sorting,
        props.client,
        props.onSearch,
        props.projectKey,
        props.refetchKey,
    ]);

    return (
        <Query query={SYNC_DATA} displayName="SyncDataQuery">
            {({
                loading: syncDataLoading,
                error: syncDataError,
                data: syncData,
                networkStatus: syncDataNetworkStatus,
            }) => {
                if (syncDataLoading || syncDataNetworkStatus === 4) return <Loading />;
                if (syncDataError) return `Error! ${syncDataError.message}`;
                if (!syncData || !syncData.syncData) {
                    return (
                        <SyncView>
                            <div>
                                <img alt="sync" src={`./sync.png`} />
                                <h3>Component management app is synchronizing with your data.</h3>
                                <p>This might take a minute, please refresh the page.</p>
                                <Button appearance="primary" onClick={() => window.location.reload()}>
                                    Refresh
                                </Button>
                            </div>
                        </SyncView>
                    );
                }
                return (
                    <div>
                        {virtualComponentBeingDeleted && (
                            <DeleteComponentModal
                                vc={virtualComponentBeingDeleted}
                                setIsOpen={() => setVirtualComponentBeingDeleted(null)}
                                loading={deleteLoading}
                                confirm={_handleDeleteComponent.bind(this)}
                            />
                        )}
                        {virtualComponentBeingMerged && (
                            <MergeComponentModal
                                virtualComponent={virtualComponentBeingMerged}
                                setIsOpen={() => setVirtualComponentBeingMerged(null)}
                                duplicates={mergeDuplicates}
                                confirm={handleMergeComponent}
                            />
                        )}
                        <SearchRow>
                            <TableFilters
                                value={dataState.filters}
                                handleChange={handleFilterChange}
                                renderingWithinProjectContext={!!props.projectKey}
                                showGroupByLabelsToogle={false}
                            />
                        </SearchRow>
                        {isLoading && !virtualComponentsPaginated.count ? (
                            <Loading />
                        ) : (
                            <LoadingOverlay
                                active={isLoading}
                                spinner={<Spinner />}
                                styles={{
                                    overlay: (base) => ({
                                        ...base,
                                        background:
                                            colorMode === 'dark' ? 'rgba(35, 35, 40, 0.5)' : 'rgba(235, 236, 240, 0.3)',
                                    }),
                                }}
                            >
                                <VirtualComponentsTable
                                    virtualComponents={virtualComponentsWithUsers}
                                    openCreateVirtualComponentModal={openCreateVirtualComponentModal}
                                    editVirtualComponent={editVirtualComponent}
                                    setToDelete={setVirtualComponentBeingDeleted}
                                    setToMerge={setVirtualComponentBeingMerged}
                                    onChangeSorting={onChangeSorting}
                                    sorting={dataState.sorting}
                                    renderingWithinProjectContext={!!props.projectKey}
                                    filters={dataState.filters}
                                />
                            </LoadingOverlay>
                        )}
                        <PaginationContainer
                            rowsCount={virtualComponentsPaginated.count}
                            initialPageSize={dataState.pagination.pageSize}
                            onPageChange={onPageChange}
                        />

                        <FlagGroup onDismissed={dismissFlag}>
                            {flags.map((flag) => (
                                <Flag key={flag.key} {...flag} />
                            ))}
                        </FlagGroup>
                    </div>
                );
            }}
        </Query>
    );
}

export default compose(
    graphql(DELETE_VIRTUAL_COMPONENT, { name: 'deleteVirtualComponent' }),
    graphql(MERGE_VIRTUAL_COMPONENT, { name: 'mergeVirtualComponent' }),
    graphql(SET_ACCESS_AUDIT, { name: 'setAccessAudit' }),
    withApollo,
)(VirtualComponents);
